/**
 * FloorAlignmentEditor — V14.63 Sprint 26F.
 *
 * 2D Konva overlay that stacks all floors of a maison on top of each other
 * with semi-transparency so the user can align them (drag tx/ty, scale, rot).
 *
 * UX :
 *   - Each floor is rendered as a polyline / polygon group on its own Layer.
 *   - Active floor = full opacity, draggable handles.
 *   - Other floors = dimmed reference layers (locked).
 *   - Bottom controls : tx/ty sliders, scale slider, rotation slider.
 *   - The `RDC` floor is anchored at the origin and cannot be moved
 *     (everyone aligns against it).
 *
 * Saved transform shape :
 *   floor.geometry.transform = { tx, ty, scale, rot }
 *
 * Props :
 *   - floors      : list of { floor_id, name, code, level, geometry }
 *   - activeFloorId
 *   - onChangeTransform : (floorId, transform) => void
 *   - onSelectFloor     : (floorId) => void
 *   - onCommit          : () => Promise — persist all transforms to backend
 */
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Stage, Layer, Line, Circle, Text, Group, Rect } from 'react-konva';
import {
  Move, Maximize2, RotateCw, Lock, Loader2, Save, Eye, EyeOff, AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { resolveTransform, applyTransform, boundsPx } from './geomUtils';

const STAGE_W = 800;
const STAGE_H = 540;
const PAD = 24;

const FLOOR_COLORS = {
  '-2': '#0ea5e9',  // sky
  '-1': '#22d3ee',  // cyan
   '0': '#10b981',  // emerald (RDC anchor)
   '1': '#f59e0b',  // amber
   '2': '#fb7185',  // rose
   '3': '#a78bfa',  // violet
   default: '#94a3b8',
};

const colorForLevel = (level) => FLOOR_COLORS[String(level)] || FLOOR_COLORS.default;

// Compute the scale factor + offset so we fit the global bbox of all floors
// (after applying current transforms) within the stage viewport.
const fitToStage = (floors) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const f of floors) {
    const t = resolveTransform(f);
    for (const w of (f.geometry?.walls || [])) {
      [[w.x1, w.y1], [w.x2, w.y2]].forEach(([px, py]) => {
        const p = applyTransform(px, py, t);
        if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
          minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
          minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
        }
      });
    }
    for (const v of (f.geometry?.terrain || [])) {
      const x = Array.isArray(v) ? v[0] : v?.x;
      const y = Array.isArray(v) ? v[1] : v?.y;
      const p = applyTransform(x || 0, y || 0, t);
      if (Number.isFinite(p.x) && Number.isFinite(p.y)) {
        minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
        minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
      }
    }
  }
  if (!Number.isFinite(minX)) {
    return { sx: 1, sy: 1, ox: STAGE_W / 2, oy: STAGE_H / 2 };
  }
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);
  const sx = (STAGE_W - 2 * PAD) / w;
  const sy = (STAGE_H - 2 * PAD) / h;
  const s = Math.min(sx, sy);
  return {
    sx: s, sy: s,
    ox: STAGE_W / 2 - ((minX + maxX) / 2) * s,
    oy: STAGE_H / 2 - ((minY + maxY) / 2) * s,
  };
};

const FloorAlignmentEditor = ({
  floors = [],
  activeFloorId = null,
  onChangeTransform,
  onSelectFloor,
  onCommit,
  saving = false,
}) => {
  const sortedFloors = useMemo(
    () => [...floors].sort((a, b) => (b.level ?? 0) - (a.level ?? 0)),
    [floors]);
  const rdc = sortedFloors.find((f) => f.level === 0) || sortedFloors[sortedFloors.length - 1];
  const isRdc = (f) => rdc && f.floor_id === rdc.floor_id;
  const activeFloor = sortedFloors.find((f) => f.floor_id === activeFloorId) || rdc;
  const [hiddenIds, setHiddenIds] = useState(new Set());

  const fit = useMemo(() => fitToStage(sortedFloors), [sortedFloors]);

  const activeTransform = resolveTransform(activeFloor);

  // Px <-> Stage coords helpers (using the fit).
  const pxToStage = useCallback((px, py, transform) => {
    const p = applyTransform(px, py, transform);
    return { x: fit.ox + p.x * fit.sx, y: fit.oy + p.y * fit.sy };
  }, [fit]);

  const renderFloorLines = (f) => {
    if (hiddenIds.has(f.floor_id)) return null;
    const t = resolveTransform(f);
    const isActive = f.floor_id === activeFloor?.floor_id;
    const stroke = colorForLevel(f.level);
    const opacity = isActive ? 0.92 : 0.32;

    const walls = (f.geometry?.walls || []);
    const wallElems = walls.map((w) => {
      const a = pxToStage(w.x1, w.y1, t);
      const b = pxToStage(w.x2, w.y2, t);
      return (
        <Line key={`w-${f.floor_id}-${w.id}`}
              points={[a.x, a.y, b.x, b.y]}
              stroke={stroke}
              strokeWidth={isActive ? 2.5 : 1.5}
              opacity={opacity}
              lineCap="round" />
      );
    });

    // Terrain polygon as closed dashed line for the RDC.
    let terrainLine = null;
    if ((f.geometry?.terrain || []).length >= 3) {
      const pts = [];
      for (const v of f.geometry.terrain) {
        const x = Array.isArray(v) ? v[0] : v?.x;
        const y = Array.isArray(v) ? v[1] : v?.y;
        const p = pxToStage(x || 0, y || 0, t);
        pts.push(p.x, p.y);
      }
      // Close the polygon.
      if (pts.length >= 4) pts.push(pts[0], pts[1]);
      terrainLine = (
        <Line key={`t-${f.floor_id}`}
              points={pts}
              stroke={stroke}
              strokeWidth={1.2}
              opacity={opacity * 0.85}
              dash={[5, 4]}
              closed={false} />
      );
    }

    // Label at the centroid (after transform).
    const bb = boundsPx(walls, f.geometry?.terrain || []);
    const cx = (bb.minX + bb.maxX) / 2;
    const cy = (bb.minY + bb.maxY) / 2;
    const center = pxToStage(cx, cy, t);

    return (
      <Layer key={f.floor_id} listening={false}>
        {terrainLine}
        {wallElems}
        <Text x={center.x - 26} y={center.y - 8}
              text={`${f.code || f.name}`} fontSize={11}
              fill={stroke} opacity={opacity}
              fontStyle="bold" />
      </Layer>
    );
  };

  const setTransformField = (field, value) => {
    if (!activeFloor || isRdc(activeFloor)) return;
    if (onChangeTransform) onChangeTransform(activeFloor.floor_id, { ...activeTransform, [field]: value });
  };

  const resetTransform = () => {
    if (!activeFloor || isRdc(activeFloor)) return;
    if (onChangeTransform) onChangeTransform(activeFloor.floor_id, { tx: 0, ty: 0, scale: 1, rot: 0 });
  };

  const toggleHide = (floorId) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(floorId)) next.delete(floorId);
      else next.add(floorId);
      return next;
    });
  };

  return (
    <div className="rounded-lg border border-white/10 bg-black/30"
         data-testid="floor-alignment-editor">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10
                      text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/80">
        <Maximize2 className="w-3.5 h-3.5" />
        Calage inter-étages
        <span className="text-gray-500 normal-case tracking-normal ml-2">
          Empile les plans avec transparence — alignez tous les étages sur le RDC
        </span>
        <div className="flex-1" />
        <Button size="sm"
                onClick={onCommit}
                disabled={saving}
                data-testid="floor-alignment-commit-btn"
                className="h-7 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[11px]">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          {saving ? 'Sauvegarde…' : 'Sauvegarder le calage'}
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-3 p-3">
        <div className="col-span-9">
          <div className="rounded border border-white/5" style={{ background: '#070b14' }}>
            <Stage width={STAGE_W} height={STAGE_H}>
              <Layer listening={false}>
                <Rect x={0} y={0} width={STAGE_W} height={STAGE_H} fill="#070b14" />
                {/* Grid */}
                {Array.from({ length: 21 }).map((_, i) => {
                  const x = (i / 20) * STAGE_W;
                  return (
                    <Line key={`gx-${i}`} points={[x, 0, x, STAGE_H]}
                          stroke="#1e293b" strokeWidth={0.5} />
                  );
                })}
                {Array.from({ length: 14 }).map((_, i) => {
                  const y = (i / 13) * STAGE_H;
                  return (
                    <Line key={`gy-${i}`} points={[0, y, STAGE_W, y]}
                          stroke="#1e293b" strokeWidth={0.5} />
                  );
                })}
              </Layer>
              {/* Draw each floor's lines */}
              {sortedFloors.map((f) => renderFloorLines(f))}
            </Stage>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[10.5px]">
            {sortedFloors.map((f) => {
              const active = f.floor_id === activeFloor?.floor_id;
              const hidden = hiddenIds.has(f.floor_id);
              const color = colorForLevel(f.level);
              return (
                <div key={f.floor_id}
                     className={`inline-flex items-center gap-1.5 rounded px-2 py-1 border transition-colors ${
                       active ? 'bg-white/10 border-white/40' : 'bg-black/30 border-white/10'
                     }`}>
                  <button type="button"
                          onClick={() => onSelectFloor && onSelectFloor(f.floor_id)}
                          data-testid={`alignment-floor-pill-${f.code}`}
                          className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                    <span className={active ? 'text-white font-semibold' : 'text-gray-300'}>
                      {f.code || f.name}
                    </span>
                  </button>
                  {isRdc(f) && (
                    <span className="inline-flex items-center gap-0.5 text-emerald-300/80 ml-1"
                          title="Ancrage de référence — non modifiable">
                      <Lock className="w-2.5 h-2.5" /> ANCRE
                    </span>
                  )}
                  <button type="button"
                          onClick={() => toggleHide(f.floor_id)}
                          className="text-gray-400 hover:text-white ml-1"
                          data-testid={`alignment-floor-eye-${f.code}`}
                          title={hidden ? 'Afficher cet étage' : 'Masquer cet étage'}>
                    {hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="col-span-3 space-y-3 text-[11px] text-gray-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/70 mb-1">
              Étage actif
            </div>
            <div className="font-mono text-sm text-emerald-100">
              {activeFloor ? `${activeFloor.name} (${activeFloor.code})` : '—'}
            </div>
            {activeFloor && isRdc(activeFloor) && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-emerald-300/70">
                <Lock className="w-3 h-3" /> Ancrage RDC — non modifiable
              </div>
            )}
          </div>

          <SliderField
            label="Déplacement X (tx)"
            value={activeTransform.tx}
            min={-800} max={800} step={1}
            disabled={isRdc(activeFloor)}
            onChange={(v) => setTransformField('tx', v)}
            testid="alignment-slider-tx"
            unit="px"
            icon={Move}
          />
          <SliderField
            label="Déplacement Y (ty)"
            value={activeTransform.ty}
            min={-800} max={800} step={1}
            disabled={isRdc(activeFloor)}
            onChange={(v) => setTransformField('ty', v)}
            testid="alignment-slider-ty"
            unit="px"
            icon={Move}
          />
          <SliderField
            label="Échelle"
            value={activeTransform.scale}
            min={0.25} max={3} step={0.005}
            disabled={isRdc(activeFloor)}
            onChange={(v) => setTransformField('scale', v)}
            testid="alignment-slider-scale"
            unit="×"
            icon={Maximize2}
          />
          <SliderField
            label="Rotation"
            value={activeTransform.rot}
            min={-Math.PI} max={Math.PI} step={0.01}
            disabled={isRdc(activeFloor)}
            onChange={(v) => setTransformField('rot', v)}
            testid="alignment-slider-rot"
            unit="rad"
            icon={RotateCw}
          />

          <button type="button"
                  onClick={resetTransform}
                  disabled={isRdc(activeFloor)}
                  data-testid="alignment-reset-btn"
                  className="w-full h-7 mt-2 rounded text-[10.5px] border border-white/15 text-gray-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed">
            Réinitialiser le calage
          </button>

          {!isRdc(activeFloor) && (
            <div className="inline-flex items-start gap-1.5 text-[10px] text-amber-300/80 mt-2">
              <AlertCircle className="w-3 h-3 mt-0.5" />
              <span>Ajustez tx/ty/scale/rot pour superposer cet étage sur le RDC.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SliderField = ({ label, value, min, max, step, onChange, testid, unit, icon: Icon, disabled }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="inline-flex items-center gap-1 text-[10.5px] text-gray-300">
        {Icon && <Icon className="w-3 h-3 text-emerald-300/70" />} {label}
      </span>
      <span className="font-mono text-[10.5px] text-emerald-200">
        {Number(value).toFixed(unit === 'rad' ? 2 : unit === '×' ? 3 : 0)} {unit}
      </span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      disabled={disabled}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      data-testid={testid}
      className="w-full accent-emerald-400"
    />
  </div>
);

export default FloorAlignmentEditor;
